import { ColorIndex } from "@nova/component-color-finder-legend/src/colorspace"
import {
    INDEX_LENGTH_U32,
    INDEXES_PER_TILE,
    SuperIndexController,
    TILE_SIZE_U32,
    Zoom,
} from "../processing/SuperIndexController.class"
import { TileFetcher } from "./remote/ColorFinderRemotes"
import { isPromise } from "formik"

export abstract class ColorFinderTiler {
    abstract getNumFrames(): number
    abstract getTile(px: number, start: number, end: number): Uint32Array | undefined
    abstract getTileByHand(zoom: number, tileNum: number): Uint32Array | undefined
    abstract setWidth(width: number): void
    abstract getWidth(): number
    private listeners = new Set<() => void>()
    protected notifyListeners() {
        this.listeners.forEach((lsnr) => lsnr())
    }
    addTileLoadListener(listener: () => void) {
        this.listeners.add(listener)
    }
    removeTileLoadListener(listener: () => void) {
        this.listeners.delete(listener)
    }
    protected cacheKey(zoom: number, tileNum: number): CacheKey {
        return `z_${zoom}t_${tileNum}`
    }

    convertPixelToTile(px: number, start: number, end: number, out: Uint32Array) {
        if (this.getWidth() === 0 || this.getWidth() === -1)
            throw "Tried to do math before setting width"
        const requestRange = end - start
        const zoom = Math.max(
            Math.min(
                Math.floor(
                    Math.log2(
                        ((this.getNumFrames() / requestRange) * this.getWidth()) / INDEXES_PER_TILE
                    )
                ),
                Math.floor(Math.log2(this.getNumFrames() / INDEXES_PER_TILE))
            ),
            1
        )

        const numTiles = (1024 / INDEXES_PER_TILE) * Math.pow(2, zoom - 1)

        const framesPerTile = Math.max(this.getNumFrames() / numTiles, INDEXES_PER_TILE)
        const tileNum = Math.floor(((px / this.getWidth()) * requestRange + start) / framesPerTile)

        const tileIndex = Math.floor(
            (((px / this.getWidth()) * requestRange + start) / framesPerTile - tileNum) *
                INDEXES_PER_TILE
        )

        out[0] = tileNum
        out[1] = zoom
        out[2] = tileIndex
    }
}

export const initialZoomSize = 1024

type CacheKey = `z_${number}t_${number}`

export class ZoomableColorFinderTiler extends ColorFinderTiler {
    getWidth(): number {
        return this.width
    }
    private cache: Map<CacheKey, Uint32Array> = new Map()
    private loadingPromises: Map<CacheKey, Promise<Uint32Array>> = new Map()

    private innerOut = new Uint32Array(3)

    constructor(
        private numFrames: number,
        private tileFetcher: TileFetcher,
        private width: number = 0
    ) {
        super()
    }

    getNumFrames() {
        return this.numFrames
    }

    setWidth(width: number) {
        this.width = width
    }

    getTileByHand(zoom: number, tileNum: number) {
        const key = this.cacheKey(zoom, tileNum)

        if (this.cache.has(key)) {
            return this.cache.get(key)
        }

        const tile = this.getRemoteTile(key, zoom, tileNum)

        if (!isPromise(tile)) {
            return tile
        }
    }

    getTile(px: number, start: number, end: number): Uint32Array | undefined {
        this.convertPixelToTile(px, start, end, this.innerOut)
        const tileNum = this.innerOut[0]
        const zoom = this.innerOut[1]

        const key = this.cacheKey(zoom, tileNum)

        if (this.cache.has(key)) {
            return this.cache.get(key)
        }

        const tile = this.getRemoteTile(key, zoom, tileNum)

        if (!isPromise(tile)) {
            return tile
        }
    }

    private getRemoteTile(
        key: CacheKey,
        zoom: number,
        tileNum: number
    ): Uint32Array | Promise<Uint32Array> {
        if (this.loadingPromises.has(key)) {
            return this.loadingPromises.get(key)!
        }

        const tile = this.tileFetcher(zoom, tileNum)

        if (!isPromise(tile)) {
            this.cache.set(key, tile)
            return tile
        }

        const promise = (async () => {
            try {
                const tileData = await tile
                this.cache.set(key, tileData)

                this.notifyListeners()
                return tileData
            } catch (err) {
                console.error(`Failed to fetch remote tile ${key}:`, err)
                return new Uint32Array(TILE_SIZE_U32)
            } finally {
                this.loadingPromises.delete(key)
            }
        })()

        this.loadingPromises.set(key, promise)
        return promise
    }

    private getLowerResPlaceholder(tileNum: number, zoom: number): Uint32Array | undefined {
        let currentZoom = zoom - 1
        let currentTileNum = Math.floor(tileNum / 2)

        while (currentZoom >= 1) {
            const key = this.cacheKey(currentZoom, currentTileNum)

            if (this.cache.has(key)) {
                return this.cache.get(key)
            }

            currentZoom -= 1
            currentTileNum = Math.floor(currentTileNum / 2)
        }

        return undefined
    }
}

export class LegacyColorFinderTiler extends ColorFinderTiler {
    private cache: Map<CacheKey, Uint32Array> = new Map()
    private zooms: Zoom[]
    private innerOut = new Uint32Array(3)
    constructor(
        private colorIndex: ColorIndex,
        private controller: SuperIndexController,
        private width: number = -1
    ) {
        super()
        this.zooms = this.controller.getZooms()
        for (let i = 0; i < this.colorIndex.length; i += 1) {
            const bytes = this.colorIndex.bytes
            const frameBytes = bytes.subarray(
                i * INDEX_LENGTH_U32,
                i * INDEX_LENGTH_U32 + INDEX_LENGTH_U32
            )

            this.controller.processFrame(i, frameBytes)
        }
    }

    getNumFrames() {
        return this.colorIndex.length
    }

    getWidth() {
        return this.width
    }

    setWidth(width: number) {
        this.width = width
    }

    getTileByHand(zoomLevel: number, tileNum: number) {
        const zoom = this.zooms.at(zoomLevel - 1)
        if (!zoom) return

        const tile = zoom.buffer.subarray(tileNum * TILE_SIZE_U32, (tileNum + 1) * TILE_SIZE_U32)

        return tile
    }

    getTile(px: number, start: number, end: number) {
        this.convertPixelToTile(px, start, end, this.innerOut)
        const tileNum = this.innerOut[0]
        const zoom = this.innerOut[1]
        if (px === 1) document.getElementById("zoom")!.innerText = `${zoom}`

        const key = this.cacheKey(zoom, tileNum)

        if (this.cache.has(key)) {
            return this.cache.get(key)
        }

        const zoomIndex = Math.max(zoom - 1, 0)

        const tile = this.zooms[zoomIndex].buffer.subarray(
            tileNum * INDEX_LENGTH_U32 * INDEXES_PER_TILE,
            (tileNum + 1) * INDEX_LENGTH_U32 * INDEXES_PER_TILE
        )

        this.cache.set(key, tile)
        return tile
    }
}

export const extractTile = (superIndex: Uint32Array[], zoom: number, tileNum: number) => {
    return superIndex[Math.max(zoom - 1, 0)].subarray(
        tileNum * INDEX_LENGTH_U32 * INDEXES_PER_TILE,
        (tileNum + 1) * INDEX_LENGTH_U32 * INDEXES_PER_TILE
    )
}
